import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import { useIntl } from 'react-intl';
import { useEffect, useReducer } from 'react';
import { useLocation } from 'react-router-dom';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';

import Grid from '@material-ui/core/Grid';

import TransferContainer from 'container/transfer';

import { BasicDrawer } from 'components/common/drawers';
import { DynoAlertModal } from 'components/dyno-template/modals';

import { putResetTransfer, getReceiverAccount, getSenderAccount, putResetTransferConfirmation } from 'middleware/actions/transfer';
import { get } from 'utils/lodash';
import { getStatus } from 'utils/validation';
import { formatErrorMessage } from 'utils/formatter';
import { getSwiftDetailDispatcher, getSwiftOperationHour } from 'utils/transfer/swift/getter';
import { getFavouriteAccounts, putResetFavouriteTransfer } from 'middleware/actions/favourite/transfer';
import { getConfirmationDetail, getResultDetail } from 'utils/transfer/getter';
import { formatSenderAccounts, formatTransferTypes } from 'utils/transfer/formatter';
import { isSwiftOperating, isSwiftSearchInvalid } from 'utils/transfer/swift/validation';
import { getIntrabankDetailDispatcher } from 'utils/transfer/intrabank/getter';
import { getInterbankDetailDispatcher } from 'utils/transfer/interbank/getter';
import { getEWalletDetailDispatcher } from 'utils/transfer/e-wallet/getter';
import { getVirtualAccountDetailDispatcher } from 'utils/transfer/virtual-account/getter';
import { getRtgsDetailDispatcher, getSknDetailDispatcher } from 'utils/transfer/sknRtgs/getter';

import {
  E_WALLET,
  INTRABANK,
  INTERBANK,
  OWN_ACCOUNT,
  SKN_TRANSFER,
  RTGS_TRANSFER,
  SWIFT_TRANSFER,
  VIRTUAL_ACCOUNT,
  DEFAULT_TRANSFER_TYPE_VALUE,
} from 'settings/constants/transaction';

const TransferPage = ({
  userId,
  onLogout,
  isTransactionBlocked,
  scrollToRef,
  isBackToMainPage,
  setBackToMainPage,
  setTacRequested,
  handleCloseCoachmark,
  handleTutorial,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const { formatMessage } = useIntl();

  const {
    senderAccountResult,
    receiverAccountResult,
    intrabankDetail,
    intrabankConfirmation,
    intrabankTac,
    interbankDetail,
    interbankConfirmation,
    sknTransferDetail,
    sknTransferConfirmation,
    ownTransferConfirmation,
    rtgsDetail,
    rtgsConfirmation,
    ownTransferTac,
    ownTransferResult,
    intrabankResult,
    interbankResult,
    interbankTac,
    sknTac,
    sknResult,
    rtgsTac,
    rtgsResult,
    swiftTac,
    swiftAccess,
    swiftBank,
    swiftResult,
    swiftBankDetail,
    swiftLldDetail,
    swiftConfirmation,
    virtualAccountDetail,
    virtualAccountConfirmation,
    virtualAccountTac,
    virtualAccountResult,
    eWalletDetail,
    eWalletConfirmation,
    eWalletTac,
    eWalletResult,
    // favourite section
    favouriteAccount,
    favouriteSwiftSearchResult,
    checkUserSecure2u,
  } = useSelector(
    state => ({
      swiftTac: state.TransferReducer.swiftTac,
      swiftBank: state.TransferReducer.swiftBank,
      swiftAccess: state.TransferReducer.swiftAccess,
      swiftResult: state.TransferReducer.swiftResult,
      swiftBankDetail: state.TransferReducer.swiftBankDetail,
      swiftLldDetail: state.TransferReducer.swiftLldDetail,
      swiftConfirmation: state.TransferReducer.swiftConfirmation,
      interbankDetail: state.TransferReducer.interbankDetail,
      interbankConfirmation: state.TransferReducer.interbankConfirmation,
      interbankTac: state.TransferReducer.interbankTac,
      senderAccountResult: state.TransferReducer.senderAccountResult,
      receiverAccountResult: state.TransferReducer.receiverAccountResult,
      intrabankDetail: state.TransferReducer.intrabankDetail,
      intrabankConfirmation: state.TransferReducer.intrabankConfirmation,
      intrabankTac: state.TransferReducer.intrabankTac,
      sknTransferDetail: state.TransferReducer.sknTransferDetail,
      sknTransferConfirmation: state.TransferReducer.sknTransferConfirmation,
      sknTac: state.TransferReducer.sknTransferTac,
      ownTransferConfirmation: state.TransferReducer.ownTransferConfirmation,
      rtgsDetail: state.TransferReducer.rtgsDetail,
      rtgsConfirmation: state.TransferReducer.rtgsConfirmation,
      ownTransferTac: state.TransferReducer.ownTransferTac,
      ownTransferResult: state.TransferReducer.ownTransferResult,
      intrabankResult: state.TransferReducer.intrabankResult,
      interbankResult: state.TransferReducer.interbankResult,
      rtgsTac: state.TransferReducer.rtgsTac,
      sknResult: state.TransferReducer.sknTransferResult,
      rtgsResult: state.TransferReducer.rtgsResult,
      virtualAccountDetail: state.TransferReducer.virtualAccountDetail,
      virtualAccountConfirmation: state.TransferReducer.virtualAccountConfirmation,
      virtualAccountTac: state.TransferReducer.virtualAccountTac,
      virtualAccountResult: state.TransferReducer.virtualAccountResult,
      eWalletDetail: state.TransferReducer.eWalletDetail,
      eWalletConfirmation: state.TransferReducer.eWalletConfirmation,
      eWalletTac: state.TransferReducer.eWalletTac,
      eWalletResult: state.TransferReducer.eWalletResult,
      favouriteAccount: state.FavouriteTransferReducer.favouriteAccount,
      favouriteSwiftSearchResult: state.FavouriteTransferReducer.swiftSearchResult,
      checkUserSecure2u: state.Sec2uReducer.checkUserSecure2u,
    }),
    shallowEqual
  );

  const [state, setState] = useReducer((prevState, newState) => ({ ...prevState, ...newState }), {
    drawerMessage: '',
    drawerType: 'error',
    isDrawerOpen: false,
    isLogoutAlertOpen: false,
    isRedirectMainTransferPage: false,
    transferType: DEFAULT_TRANSFER_TYPE_VALUE,
    bankName: '',
    bankSwiftCode: '',
    isFavouriteTacLocked: false,
    isInitialFetchOwnAccounts: true,
  });

  const {
    bankName,
    drawerType,
    transferType,
    isDrawerOpen,
    drawerMessage,
    bankSwiftCode,
    isLogoutAlertOpen,
    isFavouriteTacLocked,
    isRedirectMainTransferPage,
    isInitialFetchOwnAccounts,
  } = state;

  const isSwiftAvailable = transferType === SWIFT_TRANSFER ? isSwiftOperating(swiftAccess) : true;
  const { isNormal, responseCode: responseCodeCheckSecure2u } = checkUserSecure2u;

  // NOTE: always fetch from API to ensure service availability
  const transferHandler = {
    [OWN_ACCOUNT]: () => dispatch(getReceiverAccount({ userId })),
    [INTRABANK]: favouriteIndex => getIntrabankDetailDispatcher({ userId, favouriteIndex, favouriteAccount, dispatch }),
    [RTGS_TRANSFER]: (favouriteIndex, isSenderIDR, productCode) =>
      getRtgsDetailDispatcher({ userId, favouriteIndex, favouriteAccount, isSenderIDR, productCode, dispatch }),
    [SKN_TRANSFER]: (favouriteIndex, isSenderIDR, productCode) =>
      getSknDetailDispatcher({ userId, favouriteIndex, favouriteAccount, isSenderIDR, productCode, dispatch }),
    [SWIFT_TRANSFER]: favouriteIndex => getSwiftDetailDispatcher({ userId, favouriteIndex, favouriteAccount, isSwiftAvailable, dispatch }),
    [INTERBANK]: (favouriteIndex, isSenderIDR) =>
      getInterbankDetailDispatcher({ userId, favouriteIndex, favouriteAccount, isSenderIDR, dispatch }),
    [VIRTUAL_ACCOUNT]: (favouriteIndex, isSenderIDR) =>
      getVirtualAccountDetailDispatcher({ userId, favouriteIndex, favouriteAccount, isSenderIDR, dispatch }),
    [E_WALLET]: (favouriteIndex, isSenderIDR, productCode) =>
      getEWalletDetailDispatcher({ userId, favouriteIndex, favouriteAccount, isSenderIDR, productCode, dispatch }),
    default: () => null,
  };

  const detail = {
    [INTERBANK]: interbankConfirmation,
    [INTRABANK]: intrabankConfirmation,
    [OWN_ACCOUNT]: ownTransferConfirmation,
    [SKN_TRANSFER]: sknTransferConfirmation,
    [RTGS_TRANSFER]: rtgsConfirmation,
    [SWIFT_TRANSFER]: swiftConfirmation,
    [VIRTUAL_ACCOUNT]: virtualAccountConfirmation,
    [E_WALLET]: eWalletConfirmation,
    default: {},
  };

  const detailResult = {
    [OWN_ACCOUNT]: receiverAccountResult,
    [INTRABANK]: intrabankDetail,
    [INTERBANK]: interbankDetail,
    [SKN_TRANSFER]: sknTransferDetail,
    [RTGS_TRANSFER]: rtgsDetail,
    [SWIFT_TRANSFER]: swiftAccess,
    [VIRTUAL_ACCOUNT]: virtualAccountDetail,
    [E_WALLET]: eWalletDetail,
    default: {},
  };

  const tacRequestDetail = {
    [INTERBANK]: interbankTac,
    [INTRABANK]: intrabankTac,
    [OWN_ACCOUNT]: ownTransferTac,
    [SKN_TRANSFER]: sknTac,
    [RTGS_TRANSFER]: rtgsTac,
    [E_WALLET]: eWalletTac,
    [SWIFT_TRANSFER]: swiftTac,
    [VIRTUAL_ACCOUNT]: virtualAccountTac,
    default: {},
  };

  const resultDetail = {
    [INTERBANK]: interbankResult,
    [INTRABANK]: intrabankResult,
    [OWN_ACCOUNT]: ownTransferResult,
    [SKN_TRANSFER]: sknResult,
    [RTGS_TRANSFER]: rtgsResult,
    [E_WALLET]: eWalletResult,
    [SWIFT_TRANSFER]: swiftResult,
    [VIRTUAL_ACCOUNT]: virtualAccountResult,
    default: {},
  };

  const confirmationDetail = detail[transferType] || detail.default;
  const transferDetail = detailResult[transferType] || detailResult.default;
  const transferResultDetail = resultDetail[transferType] || resultDetail.default;
  const transferTacDetail = tacRequestDetail[transferType] || tacRequestDetail.default;

  const isTransferConfirmed = get(confirmationDetail, 'isSuccess', false);

  // senderAccounts = transferFrom
  const initialSenderAccounts = get(senderAccountResult, 'senderAccounts', []);
  const senderAccounts = formatSenderAccounts(initialSenderAccounts);
  const isAccountSuccess = get(senderAccountResult, 'isSuccess', '');
  const accountErrorCode = !isAccountSuccess && get(senderAccountResult, 'responseCode', '');
  const isSenderAccountEmpty = isAccountSuccess && !isEmpty(senderAccountResult) && isEmpty(senderAccounts);
  // user navigate from CASA dashboard
  const accountNoFromCasa = get(location, 'state.accountNo', '');

  // transferTypes = transferTo
  const initialTransferTypes = get(senderAccountResult, 'transferTypes', []);
  const transferTypes = formatTransferTypes(initialTransferTypes, senderAccounts);

  // Swift
  const isSwiftLldDetailSuccess = get(swiftLldDetail, 'isSuccess', false);
  const swiftOperationTime = swiftAccess && getSwiftOperationHour(swiftAccess);
  const swiftOperationMessage = formatMessage({ id: 'transaction.cutoffAlert.swift' }, swiftOperationTime);

  // Transfer detail
  const { isSwiftCutOffError: isSwiftConfirmCuttoff } = getConfirmationDetail(confirmationDetail, transferType);
  const { isTacLocked, isSwiftCutOffError: isSwiftResultCutoff } = getResultDetail(transferResultDetail, transferType);

  const isServiceOnline = get(getStatus(transferDetail), 'isStatusOnline', true);
  const isDetailSuccess = get(transferDetail, 'isSuccess', false);
  const detailErrorCode = get(transferDetail, 'responseCode', '');
  const transferDetailError = !isDetailSuccess && detailErrorCode;

  const isTransferOnline = isSwiftAvailable && isServiceOnline;
  const isSwiftCutoff = isSwiftConfirmCuttoff || isSwiftResultCutoff;
  const isSwiftOffline = !isEmpty(swiftAccess) && !isSwiftAvailable && !isSwiftCutoff;

  const handleDrawerOpen = (errorMessage, type = 'error', isFavTacLocked = false) =>
    setState({
      ...state,
      isDrawerOpen: true,
      drawerMessage: errorMessage,
      drawerType: type,
      isFavouriteTacLocked: isFavTacLocked,
    });

  const handleDrawerClose = () => {
    setState({
      ...state,
      isDrawerOpen: false,
      drawerMessage: '',
      isLogoutAlertOpen: isTacLocked || isFavouriteTacLocked || (!isNormal && responseCodeCheckSecure2u === '00'),
      isRedirectMainTransferPage: isSwiftCutoff,
    });
    dispatch(putResetTransferConfirmation());
  };

  const handleCancelTransfer = () => setBackToMainPage(true);

  const updateSenderAccount = () => {
    if (isTransactionBlocked) return;
    dispatch(getSenderAccount({ userId }));
    dispatch(getFavouriteAccounts({ userId }));
  };

  const clearTransferStorage = () => {
    dispatch(putResetTransfer());
    dispatch(putResetFavouriteTransfer());
    setBackToMainPage(false);
  };

  const handleTransferTypeChange = (type, isSenderIDR, favouriteIndex = null, productCode) => {
    setState({ transferType: type });
    return (transferHandler[type] || transferHandler.default)(favouriteIndex, isSenderIDR, productCode);
  };

  // clear transfer state before getting sender account
  /*
    need to seperate useEffect deps here
    first deps for initial render/each transaction tab changes
    second deps for isBackToMainPage
  */
  useEffect(() => {
    clearTransferStorage();
    updateSenderAccount();
  }, []);

  useEffect(() => {
    if (!isBackToMainPage) return;
    clearTransferStorage();
    updateSenderAccount();
  }, [isBackToMainPage]);

  useEffect(() => {
    if (accountErrorCode) {
      handleDrawerOpen(formatErrorMessage(formatMessage, accountErrorCode));
    }
  }, [accountErrorCode, senderAccountResult]);

  useEffect(() => {
    if (transferDetailError) {
      handleDrawerOpen(formatErrorMessage(formatMessage, transferDetailError));
    }
  }, [transferDetailError, transferDetail]);

  // handle when transfer service offline
  useEffect(() => {
    if (!isEmpty(transferDetail) && !isServiceOnline) {
      handleDrawerOpen(formatErrorMessage(formatMessage, 'TRANSACTION_SERVICE_OFFLINE'));
    }
  }, [isServiceOnline, transferDetail]);

  useEffect(() => {
    // if isSwiftCutoff, show different errormessage
    if (isSwiftOffline) {
      handleDrawerOpen(swiftOperationMessage);
    }
  }, [swiftAccess, isSwiftAvailable, isSwiftOffline]);

  useEffect(() => {
    if (isSwiftSearchInvalid(swiftBank) || isSwiftSearchInvalid(favouriteSwiftSearchResult)) {
      if (!bankSwiftCode && bankName) {
        return handleDrawerOpen(formatErrorMessage(formatMessage, 'INVALID_BANK_SEARCH'));
      }
      if (!bankName && bankSwiftCode) {
        return handleDrawerOpen(formatErrorMessage(formatMessage, 'INVALID_SWIFT_SEARCH'));
      }
      return handleDrawerOpen(formatErrorMessage(formatMessage, 'INVALID_SWIFT_BANK_SEARCH'));
    }
    return null;
  }, [swiftBank, favouriteSwiftSearchResult]);

  // handle when senderAccount return empty array
  useEffect(() => {
    if (isSenderAccountEmpty) {
      handleDrawerOpen(formatErrorMessage(formatMessage, 'SERVER_ERROR'));
    }
  }, [isSenderAccountEmpty, senderAccountResult]);

  useEffect(() => {
    if (isRedirectMainTransferPage) {
      handleCancelTransfer();
    }
  }, [isRedirectMainTransferPage]);

  useEffect(() => {
    if (isTransactionBlocked) {
      handleDrawerOpen(formatErrorMessage(formatMessage, 'HALF_KYC_NON_FINANCIAL'));
    }
  }, [isTransactionBlocked]);

  useEffect(() => {
    if (!isNormal && responseCodeCheckSecure2u === '00') setState({ isLogoutAlertOpen: true });
  }, [checkUserSecure2u]);

  useEffect(() => {
    if (isInitialFetchOwnAccounts) dispatch(getReceiverAccount({ userId }));
    setState({ isInitialFetchOwnAccounts: false });
  }, []);

  return (
    <>
      <Grid container justify="center" className="transaction transfer--container">
        <TransferContainer
          onChange={setState}
          userId={userId}
          swiftBank={swiftBank}
          scrollToRef={scrollToRef}
          transferType={transferType}
          transferTypes={transferTypes}
          isSwiftOffline={isSwiftOffline}
          transferDetail={transferDetail}
          senderAccounts={senderAccounts}
          isErrorDrawerOpen={isDrawerOpen}
          setTacRequested={setTacRequested}
          isServiceOnline={isTransferOnline}
          swiftBankDetail={swiftBankDetail}
          accountNoFromCasa={accountNoFromCasa}
          favouriteAccount={favouriteAccount}
          isBackToMainPage={isBackToMainPage}
          onAccountRefresh={updateSenderAccount}
          transferTacDetail={transferTacDetail}
          onErrorDrawerOpen={handleDrawerOpen}
          swiftOperationTime={swiftOperationTime}
          onCancelTransfer={handleCancelTransfer}
          confirmationDetail={confirmationDetail}
          isTransferConfirmed={isTransferConfirmed}
          transferResultDetail={transferResultDetail}
          isTransactionBlocked={isTransactionBlocked}
          receiverAccountResult={receiverAccountResult}
          initialSenderAccounts={initialSenderAccounts}
          onTransferTypeChange={handleTransferTypeChange}
          favouriteSwiftBanks={favouriteSwiftSearchResult}
          isSwiftLldDetailSuccess={isSwiftLldDetailSuccess}
          handleTutorial={handleTutorial}
          handleCloseCoachmark={handleCloseCoachmark}
        />

        <BasicDrawer className={drawerType} isOpen={isDrawerOpen} hideBackdrop={false} onClose={handleDrawerClose}>
          <h1 className="f5 lh-copy tc white">{drawerMessage}</h1>
        </BasicDrawer>
      </Grid>

      <DynoAlertModal
        isOpen={isLogoutAlertOpen}
        onClose={onLogout}
        title={formatMessage({ id: 'transaction.logoutAlert.title' })}
        description={formatMessage({ id: 'transaction.logoutAlert.description' })}
      />
    </>
  );
};

TransferPage.defaultProps = {
  handleTutorial: null,
  handleCloseCoachmark: null,
};

TransferPage.propTypes = {
  onLogout: PropTypes.func.isRequired,
  scrollToRef: PropTypes.func.isRequired,
  setTacRequested: PropTypes.func.isRequired,
  isBackToMainPage: PropTypes.bool.isRequired,
  setBackToMainPage: PropTypes.func.isRequired,
  isTransactionBlocked: PropTypes.bool.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleTutorial: PropTypes.func,
  handleCloseCoachmark: PropTypes.func,
};

export default TransferPage;
